import {
  applyPlaceholders,
  capitalize,
  deleteTemplate,
  editTemplate,
  escapeHtml,
  extractPlaceholders,
  findTemplateById,
  getInputType,
  getSaveDialogFields,
  getTypesForDept,
  hideOverlay,
  insert,
  readTrimmedValue,
  resetInputs,
  safeDomId,
  setOverlayHtml,
  showMessage,
  state,
  createTemplate,
  loadTemplates
} from "./template-core";
import type { Template } from "./template-core";

export function refreshUI() {
  const { departments, typesByDept } = state.index;

  if (!departments.includes(state.activeDept)) {
    state.activeDept = departments[0] || "";
  }

  const deptTypes = typesByDept[state.activeDept] ?? [];
  if (!deptTypes.includes(state.activeType)) {
    state.activeType = deptTypes[0] || "";
  }

  renderTabs(departments, state.activeDept);
  renderTypes(deptTypes, state.activeType);
  renderView(state.activeDept, state.activeType);
}

export async function refreshAfterDataChange() {
  await new Promise(requestAnimationFrame);
  refreshUI();
}

export function openTemplate(tpl: Template) {
  state.selectedTemplate = tpl;

  const placeholders = extractPlaceholders(tpl.html);
  if (placeholders.length === 0) {
    insert(tpl.html);
    hideOverlay("placeholder-overlay");
    return;
  }

  showPlaceholderUI(placeholders);
}

function showPlaceholderUI(keys: string[]) {
  const overlay = document.getElementById("placeholder-overlay");
  if (!overlay) return;

  setOverlayHtml(overlay, buildPlaceholderDialog(keys));

  document.getElementById("skipTemplate")?.addEventListener("click", () => {
    hideOverlay("placeholder-overlay");

    if (!state.selectedTemplate) return;
    insert(state.selectedTemplate.html);
  });

  document.getElementById("insertTemplate")?.addEventListener("click", () => {
    if (!state.selectedTemplate) return;

    const values: Record<string, string> = {};

    keys.forEach(k => {
      const el = document.getElementById(`ph-${safeDomId(k)}`) as HTMLInputElement | null;
      if (el?.value) values[k] = el.value;
    });

    insert(applyPlaceholders(state.selectedTemplate.html, values));
    hideOverlay("placeholder-overlay");
  });
}

function buildPlaceholderDialog(keys: string[]) {
  return `
    <div class="ph-box">
      <h3>Fill fields</h3>

      ${keys.map(renderPlaceholderField).join("")}

      <div class="ph-actions">
        <button id="skipTemplate" type="button">Skip</button>
        <button id="insertTemplate" type="button">Insert</button>
      </div>
    </div>
  `;
}

function renderPlaceholderField(key: string) {
  const type = getInputType(key);
  const safeId = `ph-${safeDomId(key)}`;
  const label = escapeHtml(key);

  if (type === "date") {
    return `<div class="ph-row">
      <label for="${safeId}">${label}</label>
      <input type="date" id="${safeId}">
    </div>`;
  }

  if (type === "time") {
    return `<div class="ph-row">
      <label for="${safeId}">${label}</label>
      <input type="time" id="${safeId}">
    </div>`;
  }

  return `<div class="ph-row">
    <label for="${safeId}">${label}</label>
    <input type="text" id="${safeId}">
  </div>`;
}

export function renderTabs(departments: string[], active: string) {
  const tabs = document.getElementById("tabs");
  if (!tabs) return;

  tabs.innerHTML = "";

  departments.forEach(dept => {
    const btn = document.createElement("button");
    btn.className = "tab-button" + (dept === active ? " active" : "");
    btn.textContent = capitalize(dept);

    btn.onclick = () => {
      state.activeDept = dept;

      const deptTypes = getTypesForDept(dept);
      state.activeType = deptTypes[0] || "";

      renderTabs(departments, dept);
      renderTypes(deptTypes, state.activeType);
      renderView(state.activeDept, state.activeType);
    };

    tabs.appendChild(btn);
  });
}

export function renderTypes(types: string[], active: string) {
  const sidebar = document.getElementById("typeSidebar");
  if (!sidebar) return;

  sidebar.innerHTML = "";

  types.forEach(type => {
    const item = document.createElement("div");
    item.className = "type-item" + (type === active ? " active" : "");
    item.textContent = capitalize(type);

    item.onclick = () => {
      state.activeType = type;
      renderTypes(types, type);
      renderView(state.activeDept, state.activeType);
    };

    sidebar.appendChild(item);
  });
}

export function renderView(dept: string, type: string) {
  const content = document.getElementById("content");
  if (!content) return;

  content.innerHTML = "";

  const grouped = state.index.templatesByDeptTypeSection[dept]?.[type] ?? {};

  Object.entries(grouped).forEach(([section, sectionTemplates]) => {
    const sectionId = `section-${safeDomId(section)}`;
    const sectionBlock = document.createElement("div");
    sectionBlock.className = "section";

    const header = document.createElement("div");
    header.className = "section-header";
    header.textContent = section;
    header.dataset.sectionHeader = "true";
    header.dataset.target = sectionId;

    const body = document.createElement("div");
    body.className = "section-body";
    body.id = sectionId;
    body.style.display = "none";

    sectionTemplates.forEach(tpl => {
      const card = document.createElement("div");
      card.className = "card template-card";
      card.dataset.templateId = String(tpl.id);

      card.innerHTML = `
        <div class="title">${escapeHtml(tpl.title)}</div>
        <div class="subtitle">${escapeHtml(tpl.description || "")}</div>
        <div class="card-footer">
          <button class="action-btn edit-template-btn" type="button" data-action="edit" aria-label="Edit template">
            <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
              <path d="M14.85 2.85a1.5 1.5 0 0 1 2.12 2.12l-9.9 9.9L5 15l.13-2.07 9.72-9.98zM4 16h12v2H4v-2z"/>
            </svg>
          </button>
          <button class="action-btn delete-template-btn" type="button" data-action="delete" aria-label="Delete template">
            <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor">
              <path d="M6 7h12l-1 14H7L6 7zm3-3h6l1 2H8l1-2z"/>
            </svg>
          </button>
        </div>
      `;

      body.appendChild(card);
    });

    sectionBlock.appendChild(header);
    sectionBlock.appendChild(body);
    content.appendChild(sectionBlock);
  });
}

export function bindContentEvents() {
  if (state.contentEventsBound) return;

  const content = document.getElementById("content");
  if (!content) return;

  state.contentEventsBound = true;

  content.addEventListener("click", async event => {
    const target = event.target as HTMLElement;

    const actionBtn = target.closest("button[data-action]") as HTMLButtonElement | null;
    if (actionBtn) {
      event.stopPropagation();

      const card = actionBtn.closest("[data-template-id]") as HTMLElement | null;
      const templateId = Number(card?.dataset.templateId);
      const tpl = Number.isFinite(templateId) ? findTemplateById(templateId) : null;

      if (!tpl) return;

      try {
        if (actionBtn.dataset.action === "edit") {
          const selectedText = await getSelectedText();
          if (!selectedText) {
            console.log("No selected text found for editing");
            return;
          }

          await editTemplate(tpl.id, `<div>${selectedText}</div>`);
          await refreshAfterDataChange();
        } else if (actionBtn.dataset.action === "delete") {
          await deleteTemplate(tpl.id);
          await refreshAfterDataChange();
        }
      } catch (error) {
        console.error("Template action failed:", error);
      }

      return;
    }

    const header = target.closest("[data-section-header='true']") as HTMLElement | null;
    if (header) {
      const sectionId = header.dataset.target;
      if (!sectionId) return;

      const body = document.getElementById(sectionId);
      if (!body) return;

      const open = body.style.display !== "block";
      body.style.display = open ? "block" : "none";
      header.classList.toggle("open", open);
      return;
    }

    const card = target.closest("[data-template-id]") as HTMLElement | null;
    if (card) {
      const templateId = Number(card.dataset.templateId);
      const tpl = Number.isFinite(templateId) ? findTemplateById(templateId) : null;
      if (tpl) openTemplate(tpl);
    }
  });
}

export function openSaveDialog() {
  const overlay = document.getElementById("save-overlay");
  if (!overlay) return;

  setOverlayHtml(overlay, buildSaveDialog());

  document.getElementById("cancelBtn")?.addEventListener("click", closeSaveDialog);
  document.getElementById("saveBtn")?.addEventListener("click", async () => {
    await saveTemplateForActiveDept(state.activeDept, state.activeType);
  });
}

function buildSaveDialog() {
  return `
    <div class="save-box">
      <h3>Neue Vorlage speichern</h3>

      <div class="save-section-dialog">
        <input
          id="saveTitle"
          type="text"
          placeholder="Titel der Vorlage (z. B. Begruessungs-Mail)"
          required
        />

        <input
          id="saveDescription"
          type="text"
          placeholder="Kurze Beschreibung der Vorlage"
        />

        <input
          id="saveDepartment"
          type="text"
          placeholder="Abteilung (leer = aktuelle Abteilung)"
        />

        <input
          id="saveSection"
          type="text"
          placeholder="Bereich (z. B. Follow-up, Angebot, Info)"
          required
        />

        <input
          id="saveType"
          type="text"
          placeholder="Typ / Kategorie (optional)"
        />

        <div class="save-actions">
          <button id="cancelBtn" type="button">Abbrechen</button>
          <button id="saveBtn" type="button">Vorlage speichern</button>
        </div>
      </div>
    </div>
  `;
}

export function closeSaveDialog() {
  hideOverlay("save-overlay");
}

async function saveTemplateForActiveDept(currentDept: string, currentType: string) {
  const { titleInput, descriptionInput, departmentInput, sectionInput, typeInput } = getSaveDialogFields();
  const title = readTrimmedValue(titleInput);
  const description = readTrimmedValue(descriptionInput);
  const section = readTrimmedValue(sectionInput).toLowerCase();
  const department = readTrimmedValue(departmentInput).toLowerCase() || currentDept;
  const type = readTrimmedValue(typeInput).toLowerCase() || currentType;

  if (!title || !section) return;

  const selectedText = await getSelectedText();
  if (!selectedText) {
    showMessage("Bitte zuerst Text im E-Mail-Editor markieren.");
    return;
  }

  await createTemplate({
    title,
    description,
    department,
    type,
    section,
    html: `<div>${selectedText}</div>`
  });

  await loadTemplates();
  await refreshAfterDataChange();
  closeSaveDialog();

  resetInputs(titleInput, descriptionInput, sectionInput, departmentInput, typeInput);
}

function getSelectedText(): Promise<string> {
  return new Promise((resolve) => {
    const item = Office.context.mailbox.item;

    if (!item) {
      resolve("");
      return;
    }

    (item as any).getSelectedDataAsync(Office.CoercionType.Html, (result: any) => {
      const value = result?.value;

      if (typeof value === "string") {
        resolve(value);
        return;
      }

      if (value && typeof value === "object") {
        const htmlValue = (value as any).html ?? (value as any).value ?? (value as any).data;

        if (typeof htmlValue === "string") {
          resolve(htmlValue);
          return;
        }
      }

      resolve("");
    });
  });
}
